import { computed, inject, InjectionToken } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { isEqual } from 'lodash-es';
import { catchError, distinctUntilChanged, firstValueFrom, from, map, pipe, switchMap, tap } from 'rxjs';
import { components } from 'src/app/core/services/api/generated/ix-api.types';
import { getIsNotNullableProps } from '../nullable-utils';
import { CompareById, patchObjectsShallow } from '../object-utils';
import { ProjectApiService } from '../services/project-api.service';

export type ProjectDto = components['schemas']['ProjectDto'];
export type CreateProjectParams = Parameters<typeof ProjectApiService.prototype.createProject>[0];
export type ProjectId = number | undefined;

interface ProjectState {
  currentProjectId: ProjectId;
  projects: ProjectDto[];
  isLoading: boolean;
}

const ProjectInitialState: ProjectState = {
  currentProjectId: undefined,
  projects: [],
  isLoading: false,
};

const ProjectStateToken = new InjectionToken<ProjectState>('ProjectState', {
  factory: () => ProjectInitialState,
});

const LS_KEY_LAST_SELECTED_PROJECT_ID = 'IX_LAST_SELECTED_PROJECT_ID';

export const ProjectStore = signalStore(
  { providedIn: 'root' },
  withState(() => inject(ProjectStateToken)),
  withComputed(({ projects, currentProjectId }) => ({
    currentProject: computed(() => getByIdOrFirst(projects(), currentProjectId())),
  })),
  withMethods((store, apiService = inject(ProjectApiService)) => ({
    async loadAll(): Promise<void> {
      patchState(store, { isLoading: true });
      const projects = await firstValueFrom(apiService.getAllProjects());
      const currentProjectIdFromStorage = Number(localStorage.getItem(LS_KEY_LAST_SELECTED_PROJECT_ID));
      const currentProjectId = getByIdOrFirst(
        projects,
        store.currentProjectId() || currentProjectIdFromStorage,
      )?.id;

      patchState(store, { projects, currentProjectId, isLoading: false });
    },

    setCurrentId: (nextId: ProjectId) => {
      if (store.currentProjectId() !== nextId) {
        localStorage.setItem(LS_KEY_LAST_SELECTED_PROJECT_ID, nextId ? nextId.toString() : '0');
        patchState(store, { currentProjectId: nextId });
      }
    },
    createProject(params: CreateProjectParams) {
      const previousProjects = store.projects();

      return firstValueFrom(
        apiService.createProject(params).pipe(
          map(project => {
            if (getIsNotNullableProps(project)) {
              return project;
            }
            throw new Error('Failed to create project.');
          }),
          tap(project => patchState(store, { projects: previousProjects.concat(project) })),
          catchError(error => {
            patchState(store, { projects: previousProjects });
            console.error(error);
            throw new Error('Failed to delete project.');
          }),
        ),
      );
    },
    updateProject(project: ProjectDto) {
      if (getIsNotNullableProps(project, 'code', 'id', 'name')) {
        const previousProjects = store.projects();
        const nextProjects = patchObjectsShallow({ patches: [project], objects: store.projects(), id: 'id' });
        patchState(store, { projects: nextProjects });

        return firstValueFrom(
          apiService
            .updateProject(
              { projectId: project.id },
              {
                code: project.code,
                name: project.name,
                docgroup: project.docGroup,
                client: project.client,
              },
            )
            .pipe(
              map(response => response as ProjectDto),
              catchError(error => {
                patchState(store, { projects: previousProjects });
                console.error(error);
                throw new Error('Failed to update project.');
              }),
            ),
        );
      }
      return Promise.reject('Invalid project. The following properties cannot be empty: code, id, name');
    },
    deleteProject(project: ProjectDto) {
      if (getIsNotNullableProps(project, 'id')) {
        const previousProjects = store.projects();
        const nextProjects = previousProjects.filter(p => !CompareById(project)(p));
        patchState(store, { projects: nextProjects });

        return firstValueFrom(
          apiService.deleteProject({ projectId: project.id }).pipe(
            catchError(error => {
              patchState(store, { projects: previousProjects });
              console.error(error);
              throw new Error('Failed to delete project.');
            }),
          ),
        );
      }
      return Promise.reject('Invalid project. The following properties cannot be empty: id');
    },
  })),
  withMethods(store => ({
    loadBySignal: rxMethod<unknown>(
      pipe(
        distinctUntilChanged(isEqual),
        switchMap(_ => from(store.loadAll())),
      ),
    ),
  })),
);

function getByIdOrFirst(projects: ProjectDto[], currentProjectId: ProjectId): ProjectDto | undefined {
  return projects.find(CompareById({ id: currentProjectId })) ?? projects.at(0);
}
